import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { SelectChangeEvent } from "@mui/material/Select";
import { MainPageBase } from "../page-bases/MainPageBase";
import { Box, Stack } from "@mui/material";
import ItemInput from "../../components/Input/ItemInput";
import ItemSelectInput from "../../components/Input/ItemSelectInput";
import FileInput from "../../components/Input/FileInput";
import { ASSET_ISSUE_LIST } from "../../config/constants/common";
import {
  ConfirmActionModal,
  ActionModal,
} from "../../components/Modals/ActionModal";
import { usePageModels } from "../../hooks/usePageActions";
import {
  PageActions,
  IAssetRecord,
  IPagination,
  IQueryPerams,
} from "../../config/types";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { DamsAppAsset } from "../../assets/registry";
import appRoutePath from "../../config/path";
import DamsTable from "../../components/Table/CollapsibleTable";
import { TRowItem } from "../../components/Table/TableRow";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { usePagination } from "../../hooks/usePagination";
import { assetApi } from "../../services/assets/asset-crud";
import { assetReportApi } from "../../services/asset-report/index";
import { formatDate } from "../../utils/common";
import { AssetLogsFilter } from "../../common-ui/page-filters/AssetLogsFilter";

interface CreateAssetReportForm {
  issue: string;
  comments: string;
  resolution: string;
  evidence_images: File[] | null;
}

const AssetReportForm = () => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pageAction, updatePageAction] = useState<PageActions>();
  const [reportImages, setReportImages] = useState<File[] | null>(null);
  const [resetFiles, setResetFiles] = useState(false);
  const [isProcessing, setProcess] = useState(false);
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const [querySet, setQuerySet] = useState<IQueryPerams>({ is_deleted: false });

  const { models, closeModel, openModel } = usePageModels();
  const navigate = useNavigate();

  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset);

  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(assetApi.getAssets);

  const {
    form,
    updateForm,
    updateInputField,
    fieldHasError,
    setSubmitAttempt,
    onFormSubmit,
  } = useDmsForm<CreateAssetReportForm>({
    issue: "",
    comments: "",
    resolution: "",
    evidence_images: null,
  });

  const populateDataSet = (data: IAssetRecord[], pagination?: IPagination) => {
    const formatData = data.map((data) => ({
      ...data,
      brand: `${data.brand}(${data.model})`,
      date_received: formatDate(data.date_received),
      warranty_in_months: data.warranty_in_months ?? "Not Stated",
      asset_meta_raw: data.asset_meta
        ? data.asset_meta.map(
            (item) => ` ${item.asset_tag}=${item.meta_value} `,
          )
        : [],
    }));
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  const fetchData = useCallback(async () => {
    const result = await assetApi.getAssets({ is_deleted: false });
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleImageSelect = (files: File[] | File | null) => {
    const filesArray = Array.isArray(files) ? files : files ? [files] : null;
    setReportImages(filesArray);
    updateForm((p) => ({
      ...p,
      evidence_images: {
        value: filesArray,
        isValid: true,
      },
    }));
    if (files) {
      setHasUnsavedChanges(true);
    }
  };

  const handleIssueSelect = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    updateForm((p) => ({
      ...p,
      issue: {
        value: value,
        isValid: value !== "",
        errorMessage: value === "" ? "Please select an issue" : "",
      },
    }));
    setHasUnsavedChanges(true);
  };

  function cancelAddItem() {
    if (hasUnsavedChanges) {
      setShowUnsavedChangesModal(true);
    } else {
      navigate(appRoutePath.ASSET_REPORT_FORM);
    }
  }

  function onSaveItemClick() {
    openModel("add");
    updatePageAction({
      type: "add",
    });
  }

  async function confirmUserSave() {
    const excludeField: (keyof CreateAssetReportForm)[] = ["evidence_images"];
    setSubmitAttempt(true);
    const { data, isValid } = onFormSubmit({ excludeFields: excludeField });

    if (isValid) {
      if (selectedRowItems.length === 0) {
        updatePageAction({
          type: pageAction?.type,
          message: "Please select at least one asset to report",
          title: "No Assets Selected",
          success: false,
        });
        setProcess(false);
        openModel("alert");
        return;
      }

      setProcess(true);

      try {
        const asset_ids = selectedRowItems.map((item) => item.id);

        const result = await assetReportApi.createAssetReport(
          {
            issue: data.issue,
            comments: data.comments,
            resolution: data.resolution,
            asset_ids: asset_ids,
          },
          reportImages || undefined,
        );

        setProcess(false);

        if (result.success) {
          updatePageAction({
            type: pageAction?.type,
            message: "Asset report has been submitted successfully",
            title: "Asset Report Submitted",
            success: true,
          });
          setHasUnsavedChanges(false);

          resetSelectedRowItems();
          updateForm((prev) => ({
            issue: { value: "", isValid: true, errorMessage: "" },
            comments: { value: "", isValid: true, errorMessage: "" },
            resolution: { value: "", isValid: true, errorMessage: "" },
            evidence_images: { value: null, isValid: true, errorMessage: "" },
          }));
          setReportImages(null);
          setResetFiles(true);

          openModel("alert");
        } else {
          updatePageAction({
            type: pageAction?.type,
            message: result.message || "Failed to submit asset report",
            title: "Submission Failed",
            success: false,
          });
          openModel("alert");
        }
      } catch (error) {
        setProcess(false);
        updatePageAction({
          type: pageAction?.type,
          message: "An error occurred while submitting the report",
          title: "Error",
          success: false,
        });
        openModel("alert");
      }
    }
  }

  function onSeeAllPress() {
    navigate(appRoutePath.ASSET_REPORT_HISTORY);
  }

  function onAddNewPress() {
    closeModel();
    resetSelectedRowItems();
    updateForm((prev) => ({
      issue: { value: "", isValid: true, errorMessage: "" },
      comments: { value: "", isValid: true, errorMessage: "" },
      resolution: { value: "", isValid: true, errorMessage: "" },
      evidence_images: { value: null, isValid: true, errorMessage: "" },
    }));
    setReportImages(null);
    setResetFiles(true);
    setHasUnsavedChanges(false);
  }

  function discardChanges() {
    setHasUnsavedChanges(false);
    setReportImages(null);
    setResetFiles(true);
    setShowUnsavedChangesModal(false);
    updateForm((prev) => ({
      issue: { value: "", isValid: true, errorMessage: "" },
      comments: { value: "", isValid: true, errorMessage: "" },
      resolution: { value: "", isValid: true, errorMessage: "" },
      evidence_images: { value: null, isValid: true, errorMessage: "" },
    }));
    resetSelectedRowItems();
  }

  function saveChanges() {
    setShowUnsavedChangesModal(false);
    onSaveItemClick();
  }

  const handleResetComplete = () => {
    setResetFiles(false);
  };

  return (
    <MainPageBase
      title={"Asset Report Form"}
      pageState={"add-item"}
      cancelBtnClick={cancelAddItem}
      addItemBtnOnClick={onSaveItemClick}
    >
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Box width="100%" maxWidth={600} m="0px 32px">
          <Stack gap={3}>
            <ItemSelectInput
              label="Issue"
              placeholder="Select Issue"
              items={ASSET_ISSUE_LIST}
              item=""
              value={form.issue.value}
              error={fieldHasError("issue")}
              handleChange={handleIssueSelect}
              errorMessage={form.issue.errorMessage}
              size="full"
              required={true}
            />

            <Stack direction="row" gap={3}>
              <ItemInput
                label="Comment"
                placeholder="Provide additional details about the asset condition or issue"
                value={form.comments.value}
                error={fieldHasError("comments")}
                handleChange={updateInputField("comments")}
                errorMessage={form.comments.errorMessage}
                multiline
                rows={5}
                size="full"
                required={true}
              />
              <ItemInput
                label="Resolution"
                placeholder="Enter how this issue will be resolved"
                value={form.resolution.value}
                error={fieldHasError("resolution")}
                handleChange={updateInputField("resolution")}
                errorMessage={form.resolution.errorMessage}
                multiline
                rows={5}
                size="full"
                required={true}
              />
            </Stack>

            <FileInput
              label="Evidence Image(s)"
              size="full"
              multiple
              onFileSelect={handleImageSelect}
              resetFiles={resetFiles}
              onResetComplete={handleResetComplete}
              name="report-images"
            />
          </Stack>
        </Box>

        <Box width="100%" mt={4}>
          <Stack width={"100%"} spacing={2}>
            <AssetLogsFilter<IAssetRecord[]>
              ftn={assetApi.getAssets}
              updateDataSet={populateDataSet}
              updateQuerySet={setQuerySet}
            />

            <DamsTable
              tableTitle="Assets"
              tableSubtitle="Select assets to report"
              useTableCheckbox={true}
              selectedRowItems={selectedRowItems}
              data={dataset}
              tableHeaders={columns}
              tableKeys={tkeys}
              hiddenKeys={hkeys}
              hiddenLabels={hcolumns}
              hasPagination
              paginationState={pagination}
              maxRowCells={8}
              onTableItemSelect={toggleRow}
              toggleSelectAllRow={toggleSelectAllRow}
              onNextPage={paginateToNextPage(querySet, populateDataSet)}
              onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
              setActivePage={paginateToPageNumber(populateDataSet, querySet)}
              tableTitleBadge={
                selectedRowItems.length === 0
                  ? undefined
                  : selectedRowItems.length + " selected"
              }
            />
          </Stack>
        </Box>
      </Box>

      <ActionModal
        show={models.showAlertModel}
        title={pageAction?.title || "Asset Report Submitted"}
        subtitle={pageAction?.message ?? ""}
        leftIcon={
          pageAction?.success === false ? DamsAppAsset.icons.warning : undefined
        }
        onLeftBtnPress={() => onSeeAllPress()}
        leftBtnTxt={"See All"}
        onRightBtnPress={() => onAddNewPress()}
        rightBtnTxt={pageAction?.type === "add" ? "Add New" : "Continue"}
        onClosePress={closeModel}
      />

      <ConfirmActionModal
        show={models.showAddModel}
        title={"Submit Asset Report"}
        subtitle={`You are submitting a new Asset Report for ${selectedRowItems.length} asset${selectedRowItems.length !== 1 ? "s" : ""}. Click "Confirm" to Submit`}
        onRightBtnPress={() => confirmUserSave()}
        onRightBtnLoading={isProcessing}
        rightBtnTxt={"Confirm"}
        onLeftBtnPress={closeModel}
        onClosePress={closeModel}
        leftBtnTxt={"Cancel"}
      />

      <ConfirmActionModal
        show={showUnsavedChangesModal}
        title={"Unsaved Changes"}
        subtitle={`Do you want to save changes for your asset report or discard them?`}
        onRightBtnPress={() => saveChanges()}
        rightBtnTxt={"Save Changes"}
        onLeftBtnPress={() => discardChanges()}
        leftBtnTxt={"Discard"}
        onClosePress={() => setShowUnsavedChangesModal(false)}
      />
    </MainPageBase>
  );
};

const tkeys = [
  "asset_category_name",
  "serial_number",
  "brand",
  "embossment_number",
  "assigned",
  "date_received",
];

const columns = [
  "Asset Category",
  "Serial Number",
  "Brand (Model)",
  "Embossment Number",
  "Assignment Status",
  "Date Received",
];

const hkeys: (keyof IAssetRecord)[] = [
  "batch_number",
  "current_location",
  "current_location_domain",
  "description",
  "warranty_in_months",
  "asset_meta_raw",
];

const hcolumns = [
  "Batch Number",
  "Current Location",
  "current location domain",
  "Description",
  "Warranty (months)",
  "Additional Information",
];

export default AssetReportForm;
